import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus, faTrash, faDollarSign, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import ColumnVisibilityModal from '../../components/ColumnVisibilityModal';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import Toast from '../../components/Toast';
import ConfirmModal from '../../components/ConfirmModal';
import { withPermissionAndLevel } from '../../components/withPermission';
import produtoService from '../../services/produtoService';
import { extensaoService } from '../../services/extensaoService';
import ProdutoPrecoService from '../../services/produtoPreco.service';

const columns = [
  { Header: 'ID', accessor: 'idProdutoPrecoExtensao' },
  { Header: 'Produto', accessor: 'produtoNome' },
  { Header: 'Extensão', accessor: 'extensaoNome' },
  { Header: 'Preço Padrão', accessor: 'precoPadrao', Cell: value => value != null ? Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '' },
  { Header: 'Preço Customizado', accessor: 'valor', Cell: value => value != null ? Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '' },
  { Header: 'Diferença', accessor: 'diferenca', Cell: (value, row) => {
    if (!row.precoPadrao || !row.valor) return '';
    const diff = row.valor - row.precoPadrao;
    const diffPercent = ((diff / row.precoPadrao) * 100).toFixed(1);
    const color = diff > 0 ? 'text-red-600' : diff < 0 ? 'text-green-600' : 'text-gray-600';
    return (
      <span className={color}>
        {diff > 0 ? '+' : ''}{Number(diff).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} ({diff > 0 ? '+' : ''}{diffPercent}%)
      </span>
    );
  }},
  { Header: 'Status', accessor: 'ativo', Cell: value => (
    <span className={`px-2 py-1 rounded text-xs font-semibold ${value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
      {value ? 'Ativo' : 'Inativo'}
    </span>
  )},
  { Header: 'Cadastrado Por', accessor: 'cadastradoPor' },
  { Header: 'Alterado Por', accessor: 'alteradoPor' },
  { Header: 'Cadastrado Em', accessor: 'cadastradoEm', Cell: value => {
    if (!value) return '';
    const date = new Date(value);
    const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return adjustedDate.toLocaleDateString('pt-BR');
  }},
  { Header: 'Alterado Em', accessor: 'alteradoEm', Cell: value => {
    if (!value) return '';
    const date = new Date(value);
    const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return adjustedDate.toLocaleDateString('pt-BR');
  }},
];

const pageSizes = [10, 20, 25, 50, 100, 200];

const ProdutoPrecosCustomizados = () => {
  const [precos, setPrecos] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [extensoes, setExtensoes] = useState([]);
  const [filters, setFilters] = useState({ search: '', produto: '', extensao: '', ativo: '' });
  const [debouncedFilters, setDebouncedFilters] = useState({ search: '', produto: '', extensao: '', ativo: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' ou 'edit'
  const [selectedPreco, setSelectedPreco] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [precoToDelete, setPrecoToDelete] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(
    columns
      .filter(col => !['cadastradoPor', 'alteradoPor', 'cadastradoEm', 'alteradoEm'].includes(col.accessor))
      .map(col => col.accessor)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [formData, setFormData] = useState({
    produto: '',
    extensao: '',
    valor: '',
    ativo: true
  });
  const [formLoading, setFormLoading] = useState(false);
  
  // Estados para cadastro em massa
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkFormData, setBulkFormData] = useState({
    produtos: [],
    extensoes: [],
    valorTipo: 'fixo', // 'fixo' ou 'percentual'
    valor: '',
    ativo: true,
    permitirAtualizacao: false // Se true, atualiza preços existentes
  });
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null); // Para mostrar prévia de criação/atualização

  const debounceTimer = useRef(null);

  // Debounce dos filtros
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedFilters(filters);
      setPage(1);
    }, 600);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [filters]);

  // Carregar dados iniciais
  useEffect(() => {
    loadSelectData();
  }, []);

  // Carregar preços quando filtros mudarem
  useEffect(() => {
    loadPrecos();
  }, [page, pageSize, debouncedFilters]);

  const loadSelectData = async () => {
    try {
      const [produtosRes, extensoesRes] = await Promise.all([
        produtoService.list({ limit: 1000, ativo: true }),
        extensaoService.list({ limit: 1000, ativo: true })
      ]);
      
      // Produtos: response.data tem { produtos: [...], pagination: {...} }
      const produtosArray = produtosRes?.data?.produtos || produtosRes?.produtos || [];
      setProdutos(produtosArray);
      
      // Extensões: response tem { success: true, data: [...] } - o array está diretamente em .data
      const extensoesArray = Array.isArray(extensoesRes?.data) 
        ? extensoesRes.data 
        : extensoesRes?.data?.extensoes || extensoesRes?.extensoes || [];
      setExtensoes(extensoesArray);
      
      console.log('✅ Produtos carregados:', produtosArray.length);
      console.log('✅ Extensões carregadas:', extensoesArray.length);
      console.log('📦 Estrutura extensoesRes:', extensoesRes);
      console.log('📦 Extensões extraídas:', extensoesArray.slice(0, 2)); // Mostra as 2 primeiras
    } catch (err) {
      console.error('❌ Erro ao carregar dados:', err);
      console.error('❌ Detalhes:', err.message);
      setToast({ show: true, message: 'Erro ao carregar produtos e extensões', type: 'error' });
    }
  };

  const loadPrecos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Montar params apenas com valores não vazios
      const params = {
        page,
        limit: pageSize
      };
      
      // Adicionar filtros apenas se tiverem valor
      if (debouncedFilters.search) {
        params.search = debouncedFilters.search;
      }
      if (debouncedFilters.produto) {
        params.produto = debouncedFilters.produto;
      }
      if (debouncedFilters.extensao) {
        params.extensao = debouncedFilters.extensao;
      }
      if (debouncedFilters.ativo !== '') {
        params.ativo = debouncedFilters.ativo;
      }
      
      console.log('🔍 Buscando preços com params:', params);
      
      const response = await ProdutoPrecoService.list(params);
      
      console.log('📦 Resposta completa da API:', response);
      console.log('📦 Estrutura response.data:', response?.data);
      
      // Backend retorna: { success: true, data: [...], pagination: {...} }
      // response é o axios response, então: response.data.data = array de preços
      const precosArray = Array.isArray(response?.data?.data) 
        ? response.data.data 
        : response?.data?.precos || [];
      
      console.log('📦 Array de preços extraído:', precosArray);
      
      // Mapear campos do backend para o formato esperado pelo frontend
      const precosMapeados = precosArray.map(preco => ({
        ...preco,
        precoPadrao: preco.produtoValorPadrao, // Mapear produtoValorPadrao para precoPadrao
        diferenca: preco.produtoValorPadrao && preco.valor 
          ? preco.valor - preco.produtoValorPadrao 
          : 0
      }));
      
      console.log('📦 Preços mapeados:', precosMapeados.slice(0, 2));
      setPrecos(precosMapeados);
      
      const paginationData = response?.data?.pagination || {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      };
      setPagination(paginationData);
      
      console.log('✅ Preços carregados:', precosArray.length);
      console.log('📊 Paginação:', paginationData);
    } catch (err) {
      console.error('❌ Erro ao carregar preços:', err);
      console.error('❌ Response:', err.response);
      setError('Erro ao carregar preços');
      setPrecos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  
  const handleOpenBulkModal = () => {
    setIsBulkModalOpen(true);
    setBulkFormData({
      produtos: [],
      extensoes: [],
      valorTipo: 'fixo',
      valor: '',
      ativo: true,
      permitirAtualizacao: false
    });
    setBulkPreview(null);
  };
  
  const handleCloseBulkModal = () => {
    setIsBulkModalOpen(false);
  };
  
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  const handleFilterChange = (e) => {
    const newFilters = { ...filters, [e.target.name]: e.target.value };
    setFilters(newFilters);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleCreate = () => {
    setModalMode('create');
    setFormData({
      produto: '',
      extensao: '',
      valor: '',
      ativo: true
    });
    setIsFormModalOpen(true);
  };

  const handleEdit = (preco) => {
    setModalMode('edit');
    setSelectedPreco(preco);
    setFormData({
      produto: preco.produto,
      extensao: preco.extensao,
      valor: preco.valor,
      ativo: preco.ativo
    });
    setIsFormModalOpen(true);
  };

  const handleDeleteClick = (preco) => {
    setPrecoToDelete(preco);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await ProdutoPrecoService.delete(precoToDelete.idProdutoPrecoExtensao);
      
      setToast({ show: true, message: 'Preço excluído com sucesso!', type: 'success' });
      setShowDeleteConfirm(false);
      setPrecoToDelete(null);
      loadPrecos();
    } catch (err) {
      console.error('Erro ao excluir preço:', err);
      setToast({ show: true, message: err.response?.data?.message || 'Erro ao excluir preço', type: 'error' });
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setFormLoading(true);
      
      if (modalMode === 'create') {
        const result = await ProdutoPrecoService.create(formData);
        console.log('✅ Preço criado:', result);
        setToast({ show: true, message: 'Preço criado com sucesso!', type: 'success' });
      } else {
        const result = await ProdutoPrecoService.update(selectedPreco.idProdutoPrecoExtensao, formData);
        console.log('✅ Preço atualizado:', result);
        setToast({ show: true, message: 'Preço atualizado com sucesso!', type: 'success' });
      }
      
      setIsFormModalOpen(false);
      
      // Aguardar um pouco antes de recarregar para dar tempo do banco processar
      setTimeout(() => {
        console.log('🔄 Recarregando lista de preços...');
        loadPrecos();
      }, 300);
    } catch (err) {
      console.error('❌ Erro ao salvar preço:', err);
      console.error('❌ Response:', err.response);
      setToast({ show: true, message: err.response?.data?.message || 'Erro ao salvar preço', type: 'error' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleBulkPreview = async () => {
    // Validações
    if (bulkFormData.produtos.length === 0) {
      setToast({ show: true, message: 'Selecione pelo menos um produto', type: 'error' });
      return;
    }
    
    if (bulkFormData.extensoes.length === 0) {
      setToast({ show: true, message: 'Selecione pelo menos uma extensão', type: 'error' });
      return;
    }
    
    if (!bulkFormData.valor || isNaN(bulkFormData.valor)) {
      setToast({ show: true, message: 'Informe um valor válido', type: 'error' });
      return;
    }
    
    try {
      setBulkLoading(true);
      
      // Verificar quais combinações já existem
      const combinacoes = [];
      for (const produtoId of bulkFormData.produtos) {
        for (const extensaoId of bulkFormData.extensoes) {
          combinacoes.push({ produto: produtoId, extensao: extensaoId });
        }
      }
      
      // Buscar preços existentes para essas combinações
      const existentes = precos.filter(p => 
        bulkFormData.produtos.includes(p.produto) && 
        bulkFormData.extensoes.includes(p.extensao)
      );
      
      const totalCombinacoes = combinacoes.length;
      const totalExistentes = existentes.length;
      const totalNovos = totalCombinacoes - totalExistentes;
      
      setBulkPreview({
        total: totalCombinacoes,
        novos: totalNovos,
        existentes: totalExistentes,
        existentesList: existentes
      });
      
    } catch (err) {
      console.error('❌ Erro ao gerar prévia:', err);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    
    // Se não permitir atualização e houver preços existentes, mostrar erro
    if (bulkPreview && bulkPreview.existentes > 0 && !bulkFormData.permitirAtualizacao) {
      setToast({ 
        show: true, 
        message: `Existem ${bulkPreview.existentes} preço(s) já cadastrado(s). Marque "Permitir atualizar existentes" para continuar.`, 
        type: 'error' 
      });
      return;
    }
    
    try {
      setBulkLoading(true);
      
      // Validações
      if (bulkFormData.produtos.length === 0) {
        setToast({ show: true, message: 'Selecione pelo menos um produto', type: 'error' });
        return;
      }
      
      if (bulkFormData.extensoes.length === 0) {
        setToast({ show: true, message: 'Selecione pelo menos uma extensão', type: 'error' });
        return;
      }
      
      if (!bulkFormData.valor || isNaN(bulkFormData.valor)) {
        setToast({ show: true, message: 'Informe um valor válido', type: 'error' });
        return;
      }
      
      const result = await ProdutoPrecoService.createBulk(bulkFormData);
      
      console.log('✅ Resultado cadastro em massa:', result);
      
      // Mensagem detalhada
      const criados = result.data.success.filter(s => s.action === 'created').length;
      const atualizados = result.data.success.filter(s => s.action === 'updated').length;
      const erros = result.data.errors.length;
      
      let message = '';
      if (criados > 0) message += `${criados} criado(s)`;
      if (atualizados > 0) message += `${message ? ', ' : ''}${atualizados} atualizado(s)`;
      if (erros > 0) message += `${message ? ', ' : ''}${erros} erro(s)`;
      
      setToast({ 
        show: true, 
        message: `Processamento concluído: ${message}`, 
        type: erros > 0 ? 'warning' : 'success' 
      });
      
      setIsBulkModalOpen(false);
      setBulkPreview(null);
      
      setTimeout(() => {
        loadPrecos();
      }, 300);
    } catch (err) {
      console.error('❌ Erro ao cadastrar em massa:', err);
      setToast({ 
        show: true, 
        message: err.response?.data?.message || 'Erro ao cadastrar preços em massa', 
        type: 'error' 
      });
    } finally {
      setBulkLoading(false);
    }
  };

  // Função de exportação para Excel
  const handleExportExcel = async () => {
    try {
      setLoading(true);
      
      // Buscar TODOS os registros (sem paginação) com os filtros aplicados
      const response = await ProdutoPrecoService.list({
        ...filters,
        page: 1,
        limit: 999999 // Buscar todos
      });
      
      const data = response.data?.data || [];
      
      if (data.length === 0) {
        setToast({ show: true, message: 'Nenhum registro para exportar', type: 'warning' });
        return;
      }
      
      // Preparar dados para exportação
      const excelData = data.map(preco => {
        const row = {};
        
        // Converter valores para número - considerar os dois formatos possíveis
        const valorNum = preco.valor ? Number(preco.valor) : null;
        const precoPadraoNum = preco.precoPadrao 
          ? Number(preco.precoPadrao) 
          : (preco.produtoValorPadrao ? Number(preco.produtoValorPadrao) : null);
        
        // COLUNAS OBRIGATÓRIAS (sempre exportadas)
        row['ID'] = preco.idProdutoPrecoExtensao;
        row['Produto'] = preco.produtoNome || '-';
        row['Extensão'] = preco.extensaoNome || '-';
        row['Preço Padrão'] = precoPadraoNum ? `R$ ${precoPadraoNum.toFixed(2)}` : '-';
        row['Preço Customizado'] = valorNum ? `R$ ${valorNum.toFixed(2)}` : '-';
        
        // Calcular diferença (sempre exportada)
        if (valorNum && precoPadraoNum) {
          const diff = valorNum - precoPadraoNum;
          const percent = ((diff / precoPadraoNum) * 100).toFixed(2);
          row['Diferença'] = diff >= 0 ? `+R$ ${diff.toFixed(2)} (+${percent}%)` : `-R$ ${Math.abs(diff).toFixed(2)} (${percent}%)`;
        } else {
          row['Diferença'] = '-';
        }
        
        row['Status'] = preco.ativo ? 'Ativo' : 'Inativo';
        
        // COLUNAS OPCIONAIS (somente se visíveis)
        if (visibleColumns.includes('cadastradoPor')) row['Cadastrado Por'] = preco.cadastradoPor || '-';
        if (visibleColumns.includes('cadastradoEm')) row['Data Cadastro'] = preco.cadastradoEm ? new Date(preco.cadastradoEm).toLocaleString('pt-BR') : '-';
        if (visibleColumns.includes('alteradoPor')) row['Alterado Por'] = preco.alteradoPor || '-';
        if (visibleColumns.includes('alteradoEm')) row['Data Alteração'] = preco.alteradoEm ? new Date(preco.alteradoEm).toLocaleString('pt-BR') : '-';
        
        return row;
      });
      
      // Criar worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Ajustar largura das colunas
      const colWidths = Object.keys(excelData[0] || {}).map(key => ({
        wch: Math.max(key.length, 15)
      }));
      ws['!cols'] = colWidths;
      
      // Criar workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Preços Customizados');
      
      // Gerar nome do arquivo com data
      const dataAtual = new Date().toISOString().split('T')[0];
      const nomeArquivo = `precos_customizados_${dataAtual}.xlsx`;
      
      // Fazer download
      XLSX.writeFile(wb, nomeArquivo);
      
      setToast({ show: true, message: `${data.length} registros exportados com sucesso!`, type: 'success' });
      
    } catch (error) {
      console.error('Erro ao exportar:', error);
      setToast({ show: true, message: 'Erro ao exportar dados para Excel', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (loading && precos.length === 0) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg text-gray-600">Carregando preços customizados...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg text-red-600">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FontAwesomeIcon icon={faDollarSign} />
              Preços Diferenciados por Extensão
            </h2>
            <div className="flex gap-2">
              <button
                onClick={handleOpenBulkModal}
                className="px-3 py-2 rounded bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 flex items-center gap-2 text-sm sm:text-base"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span className="hidden sm:inline">Cadastro em Massa</span>
                <span className="sm:hidden">Massa</span>
              </button>
              <button
                onClick={handleCreate}
                className="px-3 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2 text-sm sm:text-base"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span className="hidden sm:inline">Novo Preço</span>
                <span className="sm:hidden">Novo</span>
              </button>
            </div>
          </div>

          {/* Column Visibility Modal */}
          <ColumnVisibilityModal
            columns={columns}
            visibleColumns={visibleColumns}
            onChange={handleToggleColumn}
            onClose={handleCloseModal}
            isOpen={isModalOpen}
          />

          {/* Filter Bar */}
          <div className="max-w-full mb-4">
            <div className="bg-white p-4 rounded shadow">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
                  <input
                    type="text"
                    id="search"
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                    placeholder="Produto ou extensão..."
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                </div>
                <div>
                  <label htmlFor="produto" className="block text-sm font-medium text-gray-700 mb-1">Produto</label>
                  <select
                    id="produto"
                    name="produto"
                    value={filters.produto}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400"
                  >
                    <option value="">Todos</option>
                    {produtos.map(p => (
                      <option key={p.idProduto} value={p.idProduto}>{p.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="extensao" className="block text-sm font-medium text-gray-700 mb-1">Extensão</label>
                  <select
                    id="extensao"
                    name="extensao"
                    value={filters.extensao}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400"
                  >
                    <option value="">Todas</option>
                    {extensoes.map(e => (
                      <option key={e.idExtensao} value={e.idExtensao}>{e.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="ativo" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    id="ativo"
                    name="ativo"
                    value={filters.ativo}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400"
                  >
                    <option value="">Todos</option>
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Pagination and Page Size */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
            <div className="order-2 sm:order-1 w-full sm:w-auto">
              <Pagination 
                currentPage={pagination.page} 
                totalPages={pagination.totalPages} 
                totalItems={pagination.total}
                itemsPerPage={pageSize}
                onPageChange={handlePageChange} 
              />
            </div>
            <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
              <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={e => handlePageSizeChange(Number(e.target.value))}
                className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                {pageSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-1 rounded bg-emerald-600 text-white font-medium shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                onClick={handleExportExcel}
                aria-label="Exportar para Excel"
                title="Exportar para Excel"
              >
                <FontAwesomeIcon icon={faFileExcel} />
                <span className="hidden sm:inline">Excel</span>
              </button>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                onClick={handleOpenModal}
                aria-label="Exibir/Ocultar colunas"
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Colunas</span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="max-w-full">
            <Table
              columns={columns.filter(col => visibleColumns.includes(col.accessor)).map(col => {
                if (col.accessor === 'produtoNome') {
                  return {
                    ...col,
                    Cell: (value, row) => (
                      <div className="flex items-center gap-2">
                        <span>{value}</span>
                        <button
                          onClick={() => handleEdit(row)}
                          className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                          title="Editar"
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(row)}
                          className="text-red-600 hover:text-red-800 p-1 rounded focus:outline-none"
                          title="Excluir"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    )
                  };
                }
                return col;
              })}
              data={precos}
            />
          </div>

          {pagination.total > 0 && (
            <div className="mt-4 text-sm text-gray-600 text-center">
              Mostrando {precos.length} de {pagination.total} preços customizados
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {isFormModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <form onSubmit={handleFormSubmit}>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {modalMode === 'create' ? 'Novo Preço Customizado' : 'Editar Preço Customizado'}
                </h3>
              </div>
              
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label htmlFor="form-produto" className="block text-sm font-medium text-gray-700 mb-1">
                    Produto *
                  </label>
                  <select
                    id="form-produto"
                    value={formData.produto}
                    onChange={(e) => setFormData({ ...formData, produto: e.target.value })}
                    disabled={modalMode === 'edit'}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400 disabled:bg-gray-100"
                  >
                    <option value="">Selecione o produto</option>
                    {produtos.map(p => (
                      <option key={p.idProduto} value={p.idProduto}>
                        {p.nome} - {Number(p.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="form-extensao" className="block text-sm font-medium text-gray-700 mb-1">
                    Extensão *
                  </label>
                  <select
                    id="form-extensao"
                    value={formData.extensao}
                    onChange={(e) => setFormData({ ...formData, extensao: e.target.value })}
                    disabled={modalMode === 'edit'}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400 disabled:bg-gray-100"
                  >
                    <option value="">Selecione a extensão</option>
                    {extensoes.map(e => (
                      <option key={e.idExtensao} value={e.idExtensao}>{e.nome}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="form-valor" className="block text-sm font-medium text-gray-700 mb-1">
                    Preço Customizado *
                  </label>
                  <input
                    type="number"
                    id="form-valor"
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                    step="0.01"
                    min="0"
                    required
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="form-ativo"
                    checked={formData.ativo}
                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                    className="w-4 h-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="form-ativo" className="ml-2 block text-sm text-gray-700">
                    Ativo
                  </label>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsFormModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400"
                  disabled={formLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50"
                  disabled={formLoading}
                >
                  {formLoading ? 'Salvando...' : (modalMode === 'create' ? 'Criar' : 'Salvar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Create Modal */}
      {isBulkModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseBulkModal}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-green-600 px-6 py-4">
                <h3 className="text-lg font-semibold text-white">
                  Cadastro em Massa de Preços
                </h3>
              </div>

              <form onSubmit={handleBulkSubmit}>
                <div className="px-6 py-4 space-y-4">
                  {/* Seleção de Produtos */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Produtos * (Segure Ctrl/Cmd para selecionar múltiplos)
                    </label>
                    <select
                      multiple
                      value={bulkFormData.produtos}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                        setBulkFormData({ ...bulkFormData, produtos: selected });
                      }}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 h-40"
                      required
                    >
                      <option value="">Selecione os produtos</option>
                      {produtos.map(prod => (
                        <option key={prod.idProduto} value={prod.idProduto}>
                          {prod.nome} - R$ {Number(prod.valor || 0).toFixed(2)}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {bulkFormData.produtos.length} produto(s) selecionado(s)
                    </p>
                  </div>

                  {/* Seleção de Extensões */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Extensões * (Segure Ctrl/Cmd para selecionar múltiplas)
                    </label>
                    <select
                      multiple
                      value={bulkFormData.extensoes}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                        setBulkFormData({ ...bulkFormData, extensoes: selected });
                      }}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 h-40"
                      required
                    >
                      <option value="">Selecione as extensões</option>
                      {extensoes.map(ext => (
                        <option key={ext.idExtensao} value={ext.idExtensao}>
                          {ext.nome} - {ext.cidade}/{ext.uf}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {bulkFormData.extensoes.length} extensão(ões) selecionada(s)
                    </p>
                  </div>

                  {/* Permitir Atualização */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        checked={bulkFormData.permitirAtualizacao}
                        onChange={(e) => setBulkFormData({ ...bulkFormData, permitirAtualizacao: e.target.checked })}
                        className="mr-2 mt-1"
                      />
                      <div>
                        <span className="text-sm font-medium text-yellow-900">
                          Permitir atualizar preços existentes
                        </span>
                        <p className="text-xs text-yellow-700 mt-1">
                          Se desmarcado, apenas criará preços para combinações que não existem. 
                          Se marcado, atualizará preços já cadastrados.
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Prévia */}
                  {bulkPreview && (
                    <div className={`${bulkPreview.existentes > 0 && !bulkFormData.permitirAtualizacao ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'} border rounded p-3`}>
                      <p className="text-sm font-bold mb-2">📊 Prévia do Processamento:</p>
                      <div className="space-y-1 text-sm">
                        <p>• <strong>{bulkPreview.novos}</strong> novo(s) preço(s) serão criados</p>
                        <p>• <strong>{bulkPreview.existentes}</strong> preço(s) já existem</p>
                        <p>• <strong>{bulkPreview.total}</strong> total de combinações</p>
                      </div>
                      
                      {bulkPreview.existentes > 0 && !bulkFormData.permitirAtualizacao && (
                        <p className="text-xs text-red-700 mt-2 font-medium">
                          ⚠️ Marque "Permitir atualizar existentes" para sobrescrever os {bulkPreview.existentes} preço(s) existente(s).
                        </p>
                      )}
                      
                      {bulkPreview.existentes > 0 && bulkFormData.permitirAtualizacao && (
                        <p className="text-xs text-orange-700 mt-2 font-medium">
                          ⚠️ Os {bulkPreview.existentes} preço(s) existente(s) serão ATUALIZADOS com os novos valores.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Resumo */}
                  {bulkFormData.produtos.length > 0 && bulkFormData.extensoes.length > 0 && !bulkPreview && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                      <p className="text-sm font-medium text-blue-900">
                        📊 Total: {bulkFormData.produtos.length * bulkFormData.extensoes.length} combinações
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        {bulkFormData.produtos.length} produto(s) × {bulkFormData.extensoes.length} extensão(ões)
                      </p>
                      <button
                        type="button"
                        onClick={handleBulkPreview}
                        className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
                        disabled={bulkLoading}
                      >
                        🔍 Verificar preços existentes
                      </button>
                    </div>
                  )}

                  {/* Tipo de Valor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Valor *
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="valorTipo"
                          value="fixo"
                          checked={bulkFormData.valorTipo === 'fixo'}
                          onChange={(e) => setBulkFormData({ ...bulkFormData, valorTipo: e.target.value })}
                          className="mr-2"
                        />
                        Valor Fixo (R$)
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="valorTipo"
                          value="percentual"
                          checked={bulkFormData.valorTipo === 'percentual'}
                          onChange={(e) => setBulkFormData({ ...bulkFormData, valorTipo: e.target.value })}
                          className="mr-2"
                        />
                        Percentual (%)
                      </label>
                    </div>
                  </div>

                  {/* Valor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {bulkFormData.valorTipo === 'fixo' 
                        ? 'Valor Fixo (R$) *' 
                        : 'Percentual * (valores negativos = desconto, positivos = acréscimo)'}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={bulkFormData.valor}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, valor: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
                      placeholder={bulkFormData.valorTipo === 'fixo' ? 'Ex: 55.00' : 'Ex: -10 (10% de desconto)'}
                      required
                    />
                    {bulkFormData.valorTipo === 'percentual' && (
                      <p className="text-xs text-gray-500 mt-1">
                        Exemplo: -10 = 10% de desconto | +15 = 15% de acréscimo
                      </p>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={bulkFormData.ativo}
                        onChange={(e) => setBulkFormData({ ...bulkFormData, ativo: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Ativo</span>
                    </label>
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleCloseBulkModal}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    disabled={bulkLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50"
                    disabled={bulkLoading}
                  >
                    {bulkLoading ? 'Processando...' : 'Criar Preços'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir este preço customizado?${precoToDelete ? `\n\nProduto: ${precoToDelete.produtoNome}\nExtensão: ${precoToDelete.extensaoNome}` : ''}`}
        confirmText="Excluir"
        confirmStyle="danger"
      />

      {/* Toast */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default withPermissionAndLevel(
  ProdutoPrecosCustomizados,
  'produtos.precos.listar',
  3,
  'Você não tem permissão para acessar o módulo de Preços Diferenciados.'
);
